import { Button, Dialog, DialogActions } from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import React, { useEffect, useMemo, useState } from 'react';
import { useUnsafeTranslation } from '../../../i18next';
import { fromMoment, Timestamp, toMoment } from '../../../services/time';
import { InfoBox } from '../../InfoBox';

export const DateTimeDialogUsingMoment = ({
  open,
  onClose,
  value,
  onConfirm,
  confirmLabel,
  disablePast,
  disableFuture,
  clearable,
  minutes,
  minutesStep
}: {
  open: boolean;
  onClose: () => void;
  value: moment.Moment | null;
  confirmLabel?: React.ReactNode;
  disablePast?: boolean;
  disableFuture?: boolean;
  minutes?: boolean;
  minutesStep?: number;
} & (
  | {
      clearable?: false;
      onConfirm: (nextValue: moment.Moment | null) => void;
    }
  | { clearable: true; onConfirm: (nextValue: moment.Moment | null) => void }
)) => {
  const { t } = useUnsafeTranslation();
  const [nextValue, setNextValue] = useState(value);
  useEffect(() => {
    setNextValue(value);
  }, [value]);
  return (
    <Dialog open={open} onClose={onClose}>
      <DateTimePicker
        ampm
        autoOk
        variant="static"
        openTo="date"
        value={nextValue}
        clearable={false}
        disablePast={disablePast}
        disableFuture={disableFuture}
        onChange={(m) => setNextValue(m! as any)}
        views={minutes ? ['date', 'hours', 'minutes'] : ['date', 'hours']}
        minutesStep={minutesStep}
      />
      <InfoBox style={{ textAlign: 'center' }}>
        {nextValue?.format('z')}
      </InfoBox>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        {clearable && (
          <Button
            variant="text"
            color="secondary"
            onClick={() => {
              onConfirm(null as any);
              onClose();
            }}
          >
            {t('Clear')}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onConfirm(nextValue);
            onClose();
          }}
        >
          {confirmLabel || t('Update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const DateTimeDialog = ({
  open,
  onClose,
  value,
  tz,
  onConfirm,
  confirmLabel,
  disablePast,
  disableFuture,
  clearable,
  minutes,
  minutesStep
}: {
  open: boolean;
  onClose: () => void;
  value: Timestamp | null;
  tz: string;
  confirmLabel?: React.ReactNode;
  disablePast?: boolean;
  disableFuture?: boolean;
  minutes?: boolean;
  minutesStep?: number;
} & (
  | {
      clearable: false;
      onConfirm: (nextValue: Timestamp) => void;
    }
  | { clearable: true; onConfirm: (nextValue: Timestamp | null) => void }
)) => {
  const momentValue = useMemo(() => value && toMoment(value).tz(tz), [
    value,
    tz
  ]);
  return (
    <DateTimeDialogUsingMoment
      open={open}
      onClose={onClose}
      value={momentValue}
      onConfirm={(nextMomentValue: any) =>
        onConfirm(
          nextMomentValue ? fromMoment(nextMomentValue) : nextMomentValue
        )
      }
      clearable={clearable}
      confirmLabel={confirmLabel}
      disablePast={disablePast}
      disableFuture={disableFuture}
      minutes={minutes}
      minutesStep={minutesStep}
    />
  );
};
